import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface Vendor {
  id: string;
  workspace_id: string;
  legal_name: string;
  legal_name_kana: string | null;
  display_name: string | null;
  jp_corp_number: string | null;
  jp_invoice_registration: string | null;
  jp_invoice_status: string | null;
  tax_id: string | null;
  tax_form_kind: string | null;
  tax_form_url: string | null;
  country_code: string | null;
  default_currency: string | null;
  preferred_rail: string | null;
  status: string;
  kyc_status: string;
  total_spend_cents: string;
  banking: { kind: string; fields: Record<string, string> } | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  created_at: string;
}

export function useVendors(wsId: string | undefined, filters: Record<string, string | undefined> = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
  return useQuery<Vendor[]>({
    queryKey: ['vendors', wsId, filters],
    enabled: !!wsId,
    queryFn: async () => {
      const r = await api.get<Vendor[]>(`/workspaces/${wsId}/vendors?${params.toString()}`);
      return r.data;
    },
  });
}

export interface VendorInput {
  legalName: string;
  displayName?: string;
  jpCorpNumber?: string;
  jpInvoiceRegistration?: string;
  taxId?: string;
  countryCode?: string;
  defaultCurrency?: string;
  preferredRail?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export function useCreateVendor(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: VendorInput) => (await api.post<Vendor>(`/workspaces/${wsId}/vendors`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors', wsId] }),
  });
}

export function useUpdateVendor(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<VendorInput> }) =>
      (await api.patch<Vendor>(`/workspaces/${wsId}/vendors/${id}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors', wsId] }),
  });
}

/**
 * Re-check a vendor's 適格請求書 (qualified-invoice) registration against the
 * NTA register. Server returns the freshened vendor row; we invalidate the
 * list so the status pill updates.
 */
export function useRefreshJpInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<Vendor>(`/workspaces/${wsId}/vendors/${id}/refresh-jp-invoice`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors', wsId] }),
  });
}

export interface VendorInvitation {
  id: string;
  workspace_id: string;
  vendor_id: string | null;
  email: string;
  language: string;
  invited_at: string;
  expires_at: string;
  submitted_at: string | null;
}

/** Pending + completed vendor-onboarding invitations for a workspace. */
export function useVendorInvitations(wsId: string | undefined) {
  return useQuery<VendorInvitation[]>({
    queryKey: ['vendor-invitations', wsId],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get<VendorInvitation[]>(`/workspaces/${wsId}/vendor-invitations`)).data,
  });
}

/** Mint a vendor self-serve onboarding invitation (in-app side of /vendor/onboard). */
export function useInviteVendor(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { email: string; vendorId?: string; language?: string }) =>
      (await api.post<VendorInvitation>(`/workspaces/${wsId}/vendor-invitations`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-invitations', wsId] }),
  });
}

export interface VendorSearchHit extends Vendor {
  score: number;
}

/**
 * Semantic (meaning-based) vendor search. Handles 株式会社 vs (株), spelling /
 * spacing variance, romaji vs kana. Disabled until `query` is non-empty so it
 * only fires when the user opts into smart search.
 */
export function useVendorSemanticSearch(wsId: string | undefined, query: string, enabled: boolean) {
  const q = query.trim();
  return useQuery<VendorSearchHit[]>({
    queryKey: ['vendor-semantic', wsId, q],
    enabled: !!wsId && enabled && q.length > 0,
    queryFn: async () =>
      (await api.get<VendorSearchHit[]>(`/workspaces/${wsId}/vendors/search/semantic?q=${encodeURIComponent(q)}`)).data,
  });
}
